/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

// Add third-party dependencies.
import { useMemo } from 'react';

// Add local dependencies.
import { useGetUserStudiosQuery } from '../features/studios/studiosApi';

/**
 * Studio with theme access information
 */
export interface StudioWithThemeAccess {
  id: string;
  name: string;
  role: string;
  canEditTheme: boolean;
}

/**
 * Hook to manage studio theme access for the current user
 * Returns studios where the user has owner or manager permissions
 */
export const useStudioThemeAccess = () => {
  // Get user's studios with role information
  const { data: studiosData, isLoading, error } = useGetUserStudiosQuery();

  const {
    eligibleStudios,
    hasThemeAccess,
    hasSingleStudio,
    hasMultipleStudios,
  } = useMemo(() => {
    if (!studiosData?.studios) {
      return {
        eligibleStudios: [],
        hasThemeAccess: false,
        hasSingleStudio: false,
        hasMultipleStudios: false,
      };
    }

    // Filter studios where user has owner or manager role for theme editing
    const themeEligibleStudios = studiosData.studios
      .filter(studio => {
        // Get the user role from the API response
        const userRole = (studio as any).user_role;
        // Only include studios where user is owner or manager
        return userRole === 'owner' || userRole === 'manager';
      })
      .map(studio => ({
        id: studio.id,
        name: studio.name,
        role: (studio as any).user_role,
        canEditTheme: true,
      }));

    const result = {
      eligibleStudios: themeEligibleStudios,
      hasThemeAccess: themeEligibleStudios.length > 0,
      hasSingleStudio: themeEligibleStudios.length === 1,
      hasMultipleStudios: themeEligibleStudios.length > 1,
    };

    return result;
  }, [studiosData]);

  return {
    eligibleStudios,
    isLoading,
    error,
    hasThemeAccess,
    hasSingleStudio,
    hasMultipleStudios,
  };
};
